import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useToast } from "@/hooks/use-toast";
import { Wallet, CheckCircle, ArrowLeft, Zap } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { isDevelopment, quickDevLogin } from "@/lib/devHelper";

export default function Auth() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { toast } = useToast();

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('request-auth-code', {
        body: { email },
      });

      if (error) {
        // Enhanced error messages
        if (error.message?.includes('429') || error.message?.includes('rate limit')) {
          throw new Error(t('auth.rateLimitError'));
        } else if (error.message?.includes('400') || error.message?.includes('invalid')) {
          throw new Error(t('auth.invalidEmailError'));
        } else {
          throw new Error(t('auth.serviceUnavailableError'));
        }
      }

      toast({
        title: t('auth.codeSent'),
        description: t('auth.checkInbox'),
        duration: 5000,
      });
      
      setCodeSent(true);
      setCooldown(60); // 60 second cooldown
      
      // Start cooldown timer
      const interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      toast({
        title: t('auth.couldntSendCode'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (codeValue: string) => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('verify-auth-code', {
        body: { email, code: codeValue },
      });

      if (error) throw error;

      if (!data?.session) {
        throw new Error(t('auth.noSessionError'));
      }

      // Set the session manually
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });

      if (sessionError) throw sessionError;

      toast({
        title: t('auth.welcome'),
        description: t('auth.signedIn'),
      });

      const onboardingComplete = localStorage.getItem('onboardingComplete');
      navigate(onboardingComplete ? '/' : '/onboarding');
    } catch (error: any) {
      toast({
        title: t('auth.invalidCode'),
        description: error.message || t('auth.checkCode'),
        variant: "destructive",
      });
      setCode("");
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit when code is complete
  useEffect(() => {
    if (code.length === 6 && !isLoading) {
      handleVerifyCode(code);
    }
  }, [code]);

  const handleResend = () => {
    setCodeSent(false);
    setCode("");
    setCooldown(0);
  };

  const handleDifferentEmail = () => {
    setEmail("");
    setCodeSent(false);
    setCode("");
    setCooldown(0);
  };

  const handleQuickDevLogin = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await quickDevLogin();
      
      if (error) throw error;
      
      if (data?.user) {
        toast({
          title: "🚀 Dev Login Successful",
          description: "Test account logged in automatically",
        });
        
        // Check if onboarding is complete
        const onboardingComplete = localStorage.getItem('onboardingComplete');
        navigate(onboardingComplete ? '/' : '/onboarding');
      }
    } catch (error: any) {
      toast({
        title: "Dev Login Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (codeSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-success/10 p-4">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-success/20 rounded-2xl">
                <CheckCircle className="w-12 h-12 text-success" />
              </div>
            </div>
            <h1 className="text-4xl font-bold">{t('auth.checkInbox')}</h1>
            <p className="text-lg text-muted-foreground">
              We sent a 6-digit code to
            </p>
            <p className="text-lg font-semibold text-foreground">
              {email}
            </p>
          </div>

          <div className="bg-card p-8 rounded-2xl shadow-lg border-2 space-y-6">
            <div className="space-y-4">
              <Label htmlFor="code" className="text-center block">
                Enter your code
              </Label>
              <div className="flex justify-center">
                <InputOTP 
                  maxLength={6} 
                  value={code} 
                  onChange={setCode}
                  disabled={isLoading}
                  pattern={REGEXP_ONLY_DIGITS}
                  pasteTransformer={(pasted) => pasted.replace(/\D/g, "")}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {isLoading && (
                <p className="text-sm text-muted-foreground text-center">
                  Verifying code...
                </p>
              )}
            </div>

            <div className="pt-4 space-y-3">
              <Button
                onClick={handleResend}
                variant="outline"
                className="w-full min-h-touch"
                disabled={cooldown > 0 || isLoading}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </Button>
              
              <Button
                onClick={handleDifferentEmail}
                variant="ghost"
                className="w-full min-h-touch"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('auth.differentEmail')}
              </Button>
            </div>
            
            {/* Didn't receive email helper */}
            <div className="pt-2 space-y-2 text-center">
              <p className="text-xs text-muted-foreground">
                Didn't receive the email?
              </p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Check your spam/junk folder</li>
                <li>• Verify the email address is correct</li>
                <li>• Wait a minute, it might be delayed</li>
              </ul>
            </div>
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Code expires in 10 minutes
            </p>
            <p className="text-xs text-muted-foreground">
              {t('auth.checkSpam')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-success/10 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-primary rounded-2xl">
              <Wallet className="w-12 h-12 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-4xl font-bold">{t('app.name')}</h1>
          <p className="text-lg text-muted-foreground">
            {t('auth.sendMagicLink')}
          </p>
        </div>

        <div className="bg-card p-8 rounded-2xl shadow-lg border-2">
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                required
                className="min-h-touch"
                autoFocus
              />
            </div>

            <Button
              type="submit"
              className="w-full min-h-touch text-lg"
              disabled={isLoading}
            >
              {isLoading ? "Sending code..." : "Send code"}
            </Button>
          </form>

          {isDevelopment && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground text-center font-mono">
                  🧪 Development Mode
                </p>
                <Button
                  onClick={handleQuickDevLogin}
                  variant="outline"
                  className="w-full min-h-touch bg-warning/10 hover:bg-warning/20 border-warning/30"
                  disabled={isLoading}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Quick Test Login
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Auto-login with test@cashee.dev
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="text-center space-y-1">
          <p className="text-sm text-muted-foreground">
            {t('auth.noPasswordsNeeded')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('auth.secureAuth')}
          </p>
        </div>
      </div>
    </div>
  );
}
